import { markRaw } from 'vue'
import { debounce } from 'lodash'

/**
 * Base class for shape commands
 * Commands encapsulate state changes and provide a unidirectional data flow:
 * Shape → Command → Store (no circular dependencies)
 */
class ShapeCommand {
  constructor(shape) {
    this.indexID = shape.indexID
    this.timestamp = Date.now()
  }

  execute(store) {
    throw new Error('ShapeCommand.execute() must be implemented by subclass')
  }
}

/**
 * Command to update a shape's state in the store
 */
export class UpdateShapeCommand extends ShapeCommand {
  constructor(shape) {
    super(shape)
    // Serialize shape state immediately to avoid stale data
    this.state = shape.getState()
    // Use indexID from state (in case shape.indexID wasn't set yet)
    this.indexID = this.state.indexID
  }

  execute(store) {
    // Validate indexID exists
    if (!this.indexID) {
      console.error('UpdateShapeCommand: indexID is undefined', this.state)
      return
    }
    // Use markRaw to prevent Pinia from wrapping state in Proxies
    store.shapesData.set(this.indexID, markRaw(this.state))
    store.shapesVersion.value++
  }
}

/**
 * Command to delete a shape from the store
 */
export class DeleteShapeCommand extends ShapeCommand {
  execute(store) {
    if (store.shapesData.has(this.indexID)) {
      store.shapesData.delete(this.indexID)
      store.shapesVersion.value++
    }
  }
}

/**
 * Command to update highlight state for shapes
 */
export class HighlightShapeCommand extends ShapeCommand {
  constructor(shape, highlighted) {
    super(shape)
    this.highlighted = highlighted
  }

  execute(store) {
    const shapeState = store.shapesData.get(this.indexID)
    if (shapeState) {
      store.shapesData.set(this.indexID, markRaw({
        ...shapeState,
        highlighted: this.highlighted
      }))
      store.shapesVersion.value++
    }
  }
}

/**
 * Command to update selection state for shapes
 */
export class SelectShapeCommand extends ShapeCommand {
  constructor(shape, selected) {
    super(shape)
    this.selected = selected
  }

  execute(store) {
    const shapeState = store.shapesData.get(this.indexID)
    if (shapeState) {
      store.shapesData.set(this.indexID, markRaw({
        ...shapeState,
        selected: this.selected
      }))
      store.shapesVersion.value++
    }
  }
}

/**
 * Command Queue manages batching and execution of shape commands
 * Uses debouncing to batch rapid updates and prevent recursion
 */
export class ShapeCommandQueue {
  constructor(store) {
    this.store = store
    this.queue = []

    // Debounce command execution to batch rapid updates
    // 100ms matches the original refreshShape debounce timing
    this._flush = debounce(() => this._executeAll(), 100, { trailing: true })
  }

  /**
   * Enqueue a command for execution
   * @param {ShapeCommand} command - The command to execute
   */
  enqueue(command) {
    this.queue.push(command)
    this._flush()
  }

  /**
   * Execute all queued commands in order
   * Triggers a single save after all commands complete
   */
  _executeAll() {
    const commands = this.queue.splice(0) // Clear queue

    if (commands.length === 0) return

    // Execute all commands
    commands.forEach(cmd => {
      try {
        cmd.execute(this.store)
      } catch (error) {
        console.error('Error executing shape command:', error, cmd)
      }
    })

    // Trigger single save after batch
    this.store.triggerSave()
  }

  /**
   * Immediately flush all pending commands without waiting for debounce
   * Useful for critical operations like delete or before navigation
   */
  flush() {
    this._flush.cancel() // Cancel debounce
    this._executeAll()
  }
}
